import { useState, useEffect, useMemo, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { MAX_PAGE_SIZE } from '@spoofio/shared'
import { api } from '@/services/api'
import type {
  EmailProviderName,
  EmailTemplateMeta,
  EmailTemplateDataSource,
  EmailTemplateRecordType,
} from '@/services/api'
import type { EmailProviderSetting } from '@spoofio/shared/types'
import { Card, Button, Input, Select, Badge } from '@/components/ui'
import { EMAIL_PROVIDER_OPTIONS } from '@/constants/email-providers'

// ============================================================================
// Constants & Helpers
// ============================================================================

const PREVIEW_DEBOUNCE_MS = 300

type BadgeVariant = 'success' | 'danger' | 'warning' | 'primary' | 'gray'

const CATEGORY_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  transactional: { label: 'Transactional', variant: 'primary' },
  alert: { label: 'Alert', variant: 'danger' },
  billing: { label: 'Billing', variant: 'success' },
}

// Maps a template's data source to the record kind the preview/test-send API
// accepts. `none` means the template renders only from built-in sample data, so
// no record selector is shown. `organization+subscription` hydrates from an
// organization record (the subscription is derived server-side).
const DATA_SOURCE_RECORD_TYPE: Record<EmailTemplateDataSource, EmailTemplateRecordType | null> = {
  none: null,
  organization: 'organization',
  'organization+subscription': 'organization',
  detection: 'detection',
  user: 'user',
}

const RECORD_TYPE_LABELS: Record<EmailTemplateRecordType, string> = {
  organization: 'Organization',
  detection: 'Detection',
  user: 'User',
}

type RecordOption = { id: string; label: string }

/**
 * Loads the records for the selected template's data source and normalises them
 * to `{ id, label }` options. Reuses the existing Platform Admin list endpoints;
 * only the query matching `recordType` is enabled.
 */
function useRecordOptions(recordType: EmailTemplateRecordType | null): {
  options: RecordOption[]
  isLoading: boolean
} {
  const orgs = useQuery({
    queryKey: ['email-harness-records', 'organization'],
    queryFn: () => api.adminListOrganizations({ limit: MAX_PAGE_SIZE }),
    enabled: recordType === 'organization',
  })
  const users = useQuery({
    queryKey: ['email-harness-records', 'user'],
    queryFn: () => api.adminListUsers({ limit: MAX_PAGE_SIZE }),
    enabled: recordType === 'user',
  })
  const detections = useQuery({
    queryKey: ['email-harness-records', 'detection'],
    queryFn: () => api.adminListDetections({ limit: MAX_PAGE_SIZE }),
    enabled: recordType === 'detection',
  })

  return useMemo(() => {
    if (recordType === 'organization') {
      return {
        options: (orgs.data?.data ?? []).map((o) => ({ id: o.id, label: `${o.name} (${o.slug})` })),
        isLoading: orgs.isLoading,
      }
    }
    if (recordType === 'user') {
      return {
        options: (users.data?.data ?? []).map((u) => ({ id: u.id, label: u.email })),
        isLoading: users.isLoading,
      }
    }
    if (recordType === 'detection') {
      return {
        options: (detections.data?.data ?? []).map((d) => ({
          id: d.id,
          label: `${d.fakeFqdn} — ${d.canaryName}`,
        })),
        isLoading: detections.isLoading,
      }
    }
    return { options: [], isLoading: false }
  }, [recordType, orgs.data, orgs.isLoading, users.data, users.isLoading, detections.data, detections.isLoading])
}

// ============================================================================
// Template Picker
// ============================================================================

type TemplatePickerProps = {
  templates: EmailTemplateMeta[]
  isLoading: boolean
  isError: boolean
  value: string
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  selected: EmailTemplateMeta | null
}

function TemplatePickerCard({ templates, isLoading, isError, value, onChange, selected }: TemplatePickerProps) {
  const options = useMemo(
    () => [
      { value: '', label: 'Select a template…' },
      ...templates.map((t) => ({ value: t.id, label: t.name })),
    ],
    [templates]
  )
  const badge = selected ? CATEGORY_BADGE[selected.category] : null

  return (
    <Card
      title="Email Template"
      description="Pick a template to preview or test-send. Every email Spoofio can deliver is listed here."
    >
      {isError ? (
        <p className="text-sm text-danger-600">Failed to load email templates</p>
      ) : (
        <div className="space-y-3">
          <Select
            name="template"
            label="Template"
            value={value}
            onChange={onChange}
            options={options}
            disabled={isLoading}
            className="w-full sm:w-96"
          />
          {selected && (
            <div className="flex items-start gap-3">
              {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
              <p className="text-sm text-gray-500">{selected.description}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// Record Selector (body data only — never the recipient)
// ============================================================================

function RecordSelector({ recordType, value, onSelect }: {
  recordType: EmailTemplateRecordType
  value: string
  onSelect: (recordId: string) => void
}) {
  const [filter, setFilter] = useState('')
  const { options, isLoading } = useRecordOptions(recordType)
  const label = RECORD_TYPE_LABELS[recordType]

  const selectOptions = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const matches = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options
    return [{ value: '', label: 'Use sample data' }, ...matches.map((o) => ({ value: o.id, label: o.label }))]
  }, [options, filter])

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value)
  }, [])

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onSelect(e.target.value)
    },
    [onSelect]
  )

  return (
    <Card
      title={`${label} Record`}
      description={`Choose a ${label.toLowerCase()} to render into the email body, or use sample data. The recipient is always the test address — never this record.`}
    >
      <div className="space-y-3">
        <Input
          label="Filter records"
          placeholder={`Search ${label.toLowerCase()}s…`}
          value={filter}
          onChange={handleFilterChange}
        />
        <Select
          name="record"
          label={`${label} (body data)`}
          value={value}
          onChange={handleSelectChange}
          options={selectOptions}
          disabled={isLoading}
        />
      </div>
    </Card>
  )
}

// ============================================================================
// Preview Pane (sandboxed iframe)
// ============================================================================

function useDebouncedPreviewKey(
  templateId: string,
  recordType: EmailTemplateRecordType | null,
  recordId: string
) {
  const [key, setKey] = useState({ templateId, recordType, recordId })
  useEffect(() => {
    const timer = setTimeout(() => {
      setKey({ templateId, recordType, recordId })
    }, PREVIEW_DEBOUNCE_MS)
    return () => { clearTimeout(timer) }
  }, [templateId, recordType, recordId])
  return key
}

function PreviewPane({ templateId, recordType, recordId }: {
  templateId: string
  recordType: EmailTemplateRecordType | null
  recordId: string
}) {
  const key = useDebouncedPreviewKey(templateId, recordType, recordId)

  const { data: preview, isFetching, error } = useQuery({
    queryKey: ['email-template-preview', key.templateId, key.recordType, key.recordId],
    queryFn: () =>
      api.previewEmailTemplate(key.templateId, {
        recordType: key.recordId ? key.recordType ?? undefined : undefined,
        recordId: key.recordId || undefined,
      }),
    enabled: Boolean(key.templateId),
  })

  return (
    <Card
      title="Preview"
      description="The exact subject and HTML a worker would deliver. Rendered in a sandboxed frame; all values are HTML-escaped server-side."
    >
      {error ? (
        <p className="text-sm text-danger-600">
          {error instanceof Error ? error.message : 'Failed to render preview'}
        </p>
      ) : !preview ? (
        <div className="h-96 animate-pulse rounded bg-gray-100" />
      ) : (
        <div className="space-y-3">
          <div>
            <span className="text-xs font-medium uppercase tracking-wider text-gray-400">Subject</span>
            <p className="text-sm font-medium text-gray-900" data-testid="preview-subject">
              {preview.subject}
            </p>
          </div>
          <iframe
            srcDoc={preview.html}
            sandbox=""
            title="Email preview"
            className={`h-[32rem] w-full rounded border border-gray-200 bg-white ${isFetching ? 'opacity-50' : ''}`}
          />
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// Test Send
// ============================================================================

function TestSendCard({ templateId, defaultProvider, recordType, recordId }: {
  templateId: string
  defaultProvider: EmailProviderName
  recordType: EmailTemplateRecordType | null
  recordId: string
}) {
  const [email, setEmail] = useState('')
  const [provider, setProvider] = useState<EmailProviderName>(defaultProvider)
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [resultIsError, setResultIsError] = useState(false)

  const testSend = useMutation({
    mutationFn: () =>
      api.sendEmailTemplateTest(templateId, {
        email: email.trim(),
        provider,
        recordType: recordId ? recordType ?? undefined : undefined,
        recordId: recordId || undefined,
      }),
    onSuccess: (data) => {
      setResultIsError(false)
      setResultMessage(
        data.messageId ? `Test send accepted (ID: ${data.messageId})` : 'Test send accepted'
      )
    },
    onError: (error: Error) => {
      setResultIsError(true)
      setResultMessage(error.message || 'Failed to send test email')
    },
  })

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }, [])

  const handleProviderChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvider(e.target.value as EmailProviderName)
  }, [])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!email.trim()) return
      setResultMessage(null)
      testSend.mutate()
    },
    [email, testSend]
  )

  return (
    <Card
      title="Send Test"
      description="Render this template and email it to yourself. The address below is the only recipient."
    >
      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        <div className="flex-1">
          <Input
            label="Recipient email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={handleEmailChange}
          />
        </div>
        <Select
          name="test-provider"
          label="Provider"
          value={provider}
          onChange={handleProviderChange}
          options={EMAIL_PROVIDER_OPTIONS}
          className="w-44"
        />
        <Button type="submit" variant="primary" size="sm" isLoading={testSend.isPending}>
          Send Test
        </Button>
      </form>
      {resultMessage && (
        <div
          className={`mt-3 rounded-md p-3 text-sm ${
            resultIsError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
        >
          {resultMessage}
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// Test Send Section — gated on the active-provider setting for its default
// ============================================================================

function TestSendSection({ templateId, recordType, recordId }: {
  templateId: string
  recordType: EmailTemplateRecordType | null
  recordId: string
}) {
  const { data: setting } = useQuery<EmailProviderSetting>({
    queryKey: ['email-provider-setting'],
    queryFn: () => api.getEmailProviderSetting(),
  })

  if (!setting) {
    return (
      <Card title="Send Test" description="Render this template and email it to yourself.">
        <div className="h-24 animate-pulse rounded bg-gray-100" />
      </Card>
    )
  }

  return (
    <TestSendCard
      templateId={templateId}
      defaultProvider={setting.provider}
      recordType={recordType}
      recordId={recordId}
    />
  )
}

// ============================================================================
// Email Test Harness Page
// ============================================================================

export function EmailTemplatesPage() {
  const { data: templates, isLoading, isError } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => api.listEmailTemplates(),
  })

  const [selectedId, setSelectedId] = useState('')
  const [recordId, setRecordId] = useState('')

  const selected = useMemo(
    () => templates?.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  )
  const recordType = selected ? DATA_SOURCE_RECORD_TYPE[selected.dataSource] : null

  const handleTemplateChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedId(e.target.value)
    setRecordId('') // reset body record when switching templates
  }, [])

  const handleRecordSelect = useCallback((id: string) => {
    setRecordId(id)
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Email Test Harness</h1>
        <p className="mt-1 text-sm text-gray-500">
          Preview any transactional email and send a test to yourself. Previews use
          production-safe sample data unless you pick a record.
        </p>
      </div>

      <TemplatePickerCard
        templates={templates ?? []}
        isLoading={isLoading}
        isError={isError}
        value={selectedId}
        onChange={handleTemplateChange}
        selected={selected}
      />

      {selected && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            {recordType && (
              <RecordSelector recordType={recordType} value={recordId} onSelect={handleRecordSelect} />
            )}
            <TestSendSection templateId={selected.id} recordType={recordType} recordId={recordId} />
          </div>
          <PreviewPane templateId={selected.id} recordType={recordType} recordId={recordId} />
        </div>
      )}
    </div>
  )
}
